import { useRef, useState } from 'react';

import { readFirstResultsSheet } from './readResultsWorkbook';
import {
  classifyCorporateFile,
  rejectionFor,
  type RejectedFile,
} from './rockwellCorporate';
import type { ResultsSheet } from './sheetCells';

export interface CorporateFile {
  fileName: string;
  sheet: ResultsSheet;
}

/** Reads one dropped file and says what it is, or why it is not taken. */
async function readCorporateFile(
  file: File,
): Promise<{ kind: 'saps' | 'priceBook'; file: CorporateFile } | RejectedFile> {
  let sheet: ResultsSheet;
  try {
    sheet = await readFirstResultsSheet(file);
  } catch (error) {
    return {
      fileName: file.name,
      reason: 'unreadable',
      message: error instanceof Error ? error.message : String(error),
    };
  }
  const found = classifyCorporateFile(sheet);
  if (found.kind) {
    return { kind: found.kind, file: { fileName: file.name, sheet } };
  }
  return rejectionFor(file.name, sheet, found);
}

/**
 * The corporate upload's files: one ALL FIXED SAPS workbook and any number of
 * price book templates, told apart by their headers. A later drop replaces the
 * SAPS file and adds (or replaces, by name) price books.
 */
export function useCorporateFiles() {
  const [saps, setSaps] = useState<CorporateFile | null>(null);
  const [priceBooks, setPriceBooks] = useState<CorporateFile[]>([]);
  const [isReading, setIsReading] = useState(false);
  /** Files that were neither kind, or could not be read, and why. */
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  // Only the latest drop may land: an earlier, slower read is discarded.
  const latestRead = useRef(0);

  async function readFiles(files: File[]): Promise<void> {
    latestRead.current += 1;
    const read = latestRead.current;
    setIsReading(true);
    setRejected([]);
    const books: CorporateFile[] = [];
    const bad: RejectedFile[] = [];
    let sapsFile: CorporateFile | null = null;
    // One at a time, on purpose: parsing is synchronous, so Promise.all would
    // run nothing in parallel — it would only hold every 30MB file at once.
    for (const file of files) {
      const outcome = await readCorporateFile(file);
      if (read !== latestRead.current) return;
      if ('reason' in outcome) bad.push(outcome);
      else if (outcome.kind === 'saps') sapsFile = outcome.file;
      else books.push(outcome.file);
    }
    if (sapsFile) setSaps(sapsFile);
    if (books.length > 0) {
      setPriceBooks((current) => [
        ...current.filter(
          ({ fileName }) => !books.some((book) => book.fileName === fileName),
        ),
        ...books,
      ]);
    }
    setRejected(bad);
    setIsReading(false);
  }

  function clear(): void {
    latestRead.current += 1;
    setSaps(null);
    setPriceBooks([]);
    setRejected([]);
    setIsReading(false);
  }

  return { saps, priceBooks, isReading, rejected, readFiles, clear };
}
